// src/pages/StaffManagerHandoff.js

import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { ROUTES } from "../config";
import { completeStaffManagerHandoff } from "../services/api";
import "../styles/Login.css";

const isLikelyMobile = () => {
  if (typeof window === "undefined") return false;

  const narrow = window.matchMedia?.("(max-width: 767px)")?.matches;
  const pointerCoarse = window.matchMedia?.("(pointer: coarse)")?.matches;
  return Boolean(narrow || pointerCoarse);
};

const StaffManagerHandoff = () => {
  const location = useLocation();
  const token = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return String(params.get("token") || "").trim();
  }, [location.search]);

  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [showMobileWarning, setShowMobileWarning] = useState(() => isLikelyMobile());

  const completeHandoff = async () => {
    if (!token) {
      setStatus("error");
      setMessage("This secure staff setup link is missing its token.");
      return;
    }

    setStatus("loading");
    setMessage("Opening bulk staff setup…");

    try {
      await completeStaffManagerHandoff(token);
      window.location.replace(ROUTES.STAFF_MANAGER);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus("error");
      setMessage(
        typeof detail === "string"
          ? detail
          : err?.message || "This secure staff setup link could not be used."
      );
    }
  };

  useEffect(() => {
    if (showMobileWarning) return;
    completeHandoff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMobileWarning, token]);

  if (showMobileWarning) {
    return (
      <div className="login-container page">
        <div className="login-card">
          <header className="login-header">
            <h1 className="login-title">Open this on your laptop</h1>
            <p className="login-subtitle">
              Bulk staff setup works best on a laptop or desktop, especially if your staff list is in Excel,
              Outlook, Gmail, or your school admin system.
            </p>
          </header>

          <p className="login-subtitle">
            Open this same email link on your school computer. You can continue here if you really need to,
            but the desktop experience is strongly recommended.
          </p>

          <button
            type="button"
            className="login-btn"
            onClick={() => setShowMobileWarning(false)}
          >
            Continue on this device anyway
          </button>

          <p className="login-links">
            <Link to={ROUTES.HOME} className="template-download-link login-forgot-link">
              Back to GhrianGhraffy
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container page">
      <div className="login-card">
        <header className="login-header">
          <h1 className="login-title">Bulk staff setup</h1>
          <p className="login-subtitle">
            {status === "error" ? "We could not open this secure link." : "Checking your secure staff setup link…"}
          </p>
        </header>

        {message ? (
          <p className={status === "error" ? "error-message" : "login-subtitle"} aria-live="polite">
            {message}
          </p>
        ) : null}

        {status === "error" ? (
          <p className="login-links">
            <Link to={ROUTES.LOGIN} className="template-download-link login-forgot-link">
              Sign in instead
            </Link>
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default StaffManagerHandoff;
